import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LangToggle } from "@/components/Toggles";

const AUTH_URL = "https://auth.emergentagent.com/";

const Hero = ({ t }) => (
  <div className="relative hidden lg:block overflow-hidden">
    <div className="store-bg" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/50" />
    <div className="absolute left-10 top-10">
      <div className="font-press text-5xl text-[#FFD700]" style={{ textShadow: "4px 4px 0 #000" }}>SamQuest</div>
      <div className="mt-4 text-white/90 text-lg font-semibold max-w-sm">{t("app_tag")}</div>
    </div>
  </div>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.6 34.5 26.9 35.6 24 35.6c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.6 5.6C41.4 36.8 44 31 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

export default function AuthPage() {
  const { t } = useLang();
  const { user, loginWithSession } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      setLoading(true);
      loginWithSession(decodeURIComponent(m[1]))
        .then(() => { window.history.replaceState(null, "", window.location.pathname); nav("/admin", { replace: true }); })
        .catch((e) => { window.history.replaceState(null, "", window.location.pathname); setErr(errMsg(e)); setLoading(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (user) nav("/admin"); }, [user, nav]);

  const signIn = () => {
    const redirect = `${window.location.origin}/admin/login`;
    window.location.href = `${AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <Hero t={t} />
      <div className="relative flex items-center justify-center p-6 admin-grid-bg">
        <div className="absolute top-5 right-5"><LangToggle /></div>
        <div className="w-full max-w-sm flex flex-col gap-6" data-testid="admin-login-form">
          <div>
            <div className="font-press text-lg text-primary lg:hidden mb-4">SamQuest</div>
            <h1 className="text-3xl font-extrabold tracking-tight">{t("login_title")}</h1>
            <p className="text-muted-foreground mt-2">{t("login_sub")}</p>
          </div>
          {loading ? (
            <div className="flex items-center gap-3 text-muted-foreground" data-testid="admin-login-loading">
              <Loader2 className="w-5 h-5 animate-spin" />{t("signing_in")}
            </div>
          ) : (
            <Button data-testid="admin-google-login-button" onClick={signIn}
              className="h-12 bg-white text-slate-800 hover:bg-slate-100 font-bold flex items-center justify-center gap-3">
              <GoogleIcon />{t("continue_google")}
            </Button>
          )}
          {err && <div className="text-sm text-rose-400" data-testid="admin-auth-error">{err}</div>}
          <p className="text-xs text-muted-foreground">{t("admin_access_note")}</p>
        </div>
      </div>
    </div>
  );
}
